import {
  isAllowedOrigin,
  isUiOrigin,
  makeMessage,
  parseEnvelope,
  type BridgeEnvelope,
  type OutboundType,
  type UiOrigin,
} from '@chat-emp/shared';

/**
 * Iframe-side postMessage bridge. Validates every inbound message's origin
 * against the allowlist and always posts to the parent with an explicit
 * targetOrigin (the host origin that loaded us), never '*'.
 */

// Allowed host origins are injected at build time (comma-separated).
const ALLOWED = (import.meta.env.VITE_BRIDGE_ALLOWED_ORIGINS ?? '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

type Handler = (env: BridgeEnvelope) => void;

let hostOrigin: string | null = null;

/** Best-effort host origin: document.referrer's origin, if it is allowed. */
function resolveHostOrigin(): string | null {
  if (hostOrigin) return hostOrigin;
  try {
    const ref = document.referrer ? new URL(document.referrer).origin : '';
    if (ref && isAllowedOrigin(ref, ALLOWED)) hostOrigin = ref;
  } catch {
    /* ignore */
  }
  return hostOrigin;
}

export function onBridgeMessage(handler: Handler): () => void {
  const listener = (e: MessageEvent) => {
    if (!isAllowedOrigin(e.origin, ALLOWED)) return;
    // The host page is our parent, and nothing else. An allowlisted origin only
    // says the message came from a host we trust — not that it came from the
    // page embedding us. A sibling frame or a popup at that origin can hold a
    // handle to this window and post a well-formed envelope; requiring the
    // parent is what makes "from the host" mean the host.
    if (e.source !== window.parent) return;
    const env = parseEnvelope(e.data);
    if (!env) return;
    // Pin the host origin to the first valid sender.
    if (!hostOrigin) hostOrigin = e.origin;
    handler(env);
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

export function emitToHost(type: OutboundType, payload?: unknown): void {
  const target = resolveHostOrigin();
  if (!target || window.parent === window) return;
  window.parent.postMessage(makeMessage(type, payload), target);
}

/** Split a Stream cid (`type:id`) into its parts, to match stream-chat's host
 *  events which carry channel `type` and `id` separately. */
export function cidParts(cid: string): { channelType: string; channelId: string } {
  const i = cid.indexOf(':');
  return i === -1
    ? { channelType: '', channelId: cid }
    : { channelType: cid.slice(0, i), channelId: cid.slice(i + 1) };
}

/**
 * Read a handoff token delivered via URL fragment (#handoff=...) and strip it.
 * The read is a destructive one-shot (it removes the token from the URL), so the
 * result is cached: React StrictMode double-invokes effects and any WidgetView
 * remount would otherwise read null on the second pass and hang forever.
 *
 * Only a framed load reads it, and only below an allowlisted host. A handoff
 * token is a bearer credential for whoever minted it, so a token in a URL is an
 * invitation to work inside somebody else's inbox: anyone with an employer
 * account can mint one for their own company and send the link. `widget.ts` is
 * the sole legitimate deliverer and it always injects an iframe, so requiring
 * the frame costs nothing and leaves `frame-ancestors` the one gate for both
 * this path and `init`. A top-level load falls through to `AuthGate`, which
 * already assumes a standalone URL carries no session.
 *
 * `resolveHostOrigin()` is both halves of the check: it answers only for a
 * `document.referrer` on the bridge allowlist. The widget already depends on it
 * — `emitToHost('ready')` is what unblocks the host's queued messages — so a
 * referrer this rejects is a widget that was never going to work.
 */
let cachedHandoff: string | null = null;
let hostUiOrigin: UiOrigin | undefined;

export function uiOrigin(): UiOrigin | undefined {
  return hostUiOrigin;
}

export function takeHandoffFromFragment(): string | null {
  if (cachedHandoff) return cachedHandoff;
  if (window.parent === window || !resolveHostOrigin()) return null;

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const origin = params.get('ui_origin');
  if (isUiOrigin(origin)) hostUiOrigin = origin;
  const token = params.get('handoff');
  if (token) {
    // Remove the token from the URL so it never lingers in history/referrer.
    history.replaceState(null, '', window.location.pathname + window.location.search);
    cachedHandoff = token;
  }
  return token;
}

/**
 * Drop the query from our own URL, keeping the path and the fragment. The host
 * copies its own query into our iframe src, `utm_*` and all, and we have read
 * what we need by mount. Not a full referrer control, since the asset requests
 * go out before React boots; `Referrer-Policy` owns that.
 *
 * Raw replaceState on purpose, because react-router never hears it: BrowserRouter
 * snapshots `history.location` and only re-reads on `history.listen`. That is what
 * lets views mounted later still read `?tab=` and `?channel=` from
 * `useSearchParams`, so do not swap it for `navigate(..., { replace: true })`, and
 * call it from an effect, never from render.
 *
 * The fragment stays because on `/widget` it carries the handoff token.
 */
export function scrubQueryFromUrl(): void {
  if (!window.location.search) return;
  history.replaceState(null, '', window.location.pathname + window.location.hash);
}
